<template>
    <v-skeleton-loader v-if="!record" type="article, actions" />
    <v-form v-else ref="form" v-model="isValid" class="compact-edit">
        <div class="edit-event-container">
            <KpAlert v-if="!isValid" type="error" dense>
                Achtung: Das Formular enthält Fehler. Deine Daten werden trotzdem gespeichert.
            </KpAlert>
            <v-expansion-panels accordion :value="0">
                <v-expansion-panel>
                    <v-expansion-panel-header expand-icon="ic-20 ic-col-white ic-arrowdown">
                        {{ $t('quickedit') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <QuickEditPanel
                            :record="record"
                            @propertyChange="save"
                            @locationSaved="onLocationSaved"
                            @scheduleSaved="onScheduleSaved"
                            @eventDeleted="onEventDeleted"
                        ></QuickEditPanel>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <div class="infos-container">
            <v-expansion-panels accordion :value="0">
                <v-expansion-panel>
                    <v-expansion-panel-header expand-icon="ic-20 ic-col-white ic-arrowdown">
                        {{ $t('more_infos') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <WeitereInfosPanel :record="record" @propertyChange="save"></WeitereInfosPanel>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <div class="subevents-container">
            <v-expansion-panels accordion :value="0">
                <v-expansion-panel>
                    <v-expansion-panel-header expand-icon="ic-20 ic-col-white ic-arrowdown">
                        {{ $t('sub_events') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <SubEventsPanel :record="record"></SubEventsPanel>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <div class="metadata-container">
            <v-expansion-panels accordion :value="0">
                <v-expansion-panel>
                    <v-expansion-panel-header expand-icon="ic-20 ic-col-white ic-arrowdown">
                        {{ $t('metadata') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <MetadataPanel :record="record"></MetadataPanel>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
        <div class="importdata-container">
            <v-expansion-panels accordion :value="0">
                <v-expansion-panel>
                    <v-expansion-panel-header expand-icon="ic-20 ic-col-white ic-arrowdown">
                        {{ $t('import') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <ImportdataPanel :record="record"></ImportdataPanel>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </v-form>
</template>

<script>
import { EventCollection } from '@/models/Event';
import { errToStr, setBrowserTitle, shortenString } from '@/helpers/tools';
import { mapActions, mapState } from 'vuex';
import { frontendUrl } from '@/helpers/globals';
import { userIsStaff } from '@/helpers/auth';
import BackRouteMixin from '@/mixins/backroute';
import QuickEditPanel from '@/components/event/CompactView/QuickEditPanel.vue';
import WeitereInfosPanel from '@/components/event/CompactView/WeitereInfosPanel.vue';
import SubEventsPanel from '@/components/event/CompactView/SubEventsPanel.vue';
import MetadataPanel from '@/components/event/CompactView/MetadataPanel.vue';
import ImportdataPanel from '@/components/event/CompactView/ImportdataPanel.vue';
import KpAlert from '@/components/KpAlert.vue';
import { commitToast } from '@/plugins/toast';

export default {
    name: 'EventCompactEdit',
    components: {
        QuickEditPanel,
        WeitereInfosPanel,
        SubEventsPanel,
        MetadataPanel,
        ImportdataPanel,
        KpAlert,
    },
    mixins: [BackRouteMixin],
    async beforeRouteUpdate(to, from, next) {
        await this.save();
        if (to.params.id !== from.params.id) {
            this.record = null;
        }
        this.loadRecord(to.params.id);
        next();
    },

    data() {
        return {
            isSaving: false,
            record: null,
            isValid: false,
            // activeItemId: null,
            // eventClassesWithSubEvents: [
            //     Event.CLASS_FESTIVAL,
            //     Event.CLASS_TOURNEE,
            //     Event.CLASS_EXHIBITION,
            //     Event.CLASS_PERMANENT_EXHIBITION,
            // ],
            // eventClassesWithSchedules: [
            //     Event.CLASS_EVENT,
            //     Event.CLASS_EXHIBITION,
            //     Event.CLASS_CINEMA,
            //     Event.CLASS_TOURNEE,
            // ],
        };
    },
    computed: {
        ...mapState('user', {
            user: 'record',
        }),
        browserTitle() {
            const parts = [];
            if (this.record?.title) {
                parts.push(shortenString(this.record?.title, 30), this.activeNavItem?.title);
            } else {
                const eventClass = this.record?.event_class || 'event';
                const createEventstring = this.$t('veranstaltungen_erfassen', { event_class: this.$t(eventClass) });
                parts.push(createEventstring);
            }
            parts.push(this.$t('event'));
            return parts.filter(Boolean).join(' - ');
        },
    },
    watch: {
        browserTitle: {
            handler() {
                setBrowserTitle(this.getBrowserTitle());
            },
            immediate: true,
        },
    },
    created() {
        this.saveQueue = [];
    },
    mounted() {
        this.loadRecord(this.$route.params.id);
    },
    methods: {
        ...mapActions(['runAsync']),
        userIsStaff,
        async loadRecord(id) {
            // load existing record
            try {
                const collection = new EventCollection();
                await this.runAsync(() => collection.query({ id: id, includeOlder: true }));
                const record = collection.first();
                if (record) {
                    this.record = record;
                } else {
                    this.record = null;
                }
                this.$nextTick(() => this.validateForm());
            } catch (e) {
                this.record = null;
                commitToast({ message: errToStr(e), color: 'error' });
            }
        },
        async reload() {
            this.loadRecord(this.record?.getId());
        },
        validateForm() {
            if (this.$refs.form) {
                this.$refs.form.validate();
            }
        },

        /**
         * Saving property changes: This method gets a {property, value}
         * object to be changed / saved on the active record.
         *
         * As this form is auto-saving its changes, the saves need to be queued
         * and saved to the backend synchronously (one after each change),
         * to avoid overriding properites while requests still are in progress.
         *
         * @param {{property: string, value: any}} propertyChange Object with property name and value to change
         */
        async save(propertyChange = null) {
            if (!this.record) {
                return false;
            }
            // Access check:
            if (!this.record.userCanSave(this.user)) {
                return false;
            }

            // Pushing the change to the queue:
            if (propertyChange) {
                this.saveQueue.push(propertyChange);
            }

            try {
                if (!this.isSaving) {
                    this.isSaving = true;
                    const saveNeeded = await this.executeSave();
                    if (saveNeeded) {
                        this.$emit('recordSaved', this.record);
                    }
                    this.isSaving = false;
                }
                return true;
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
                // reset queuing:
                this.isSaving = false;
                this.saveQueue = [];
            }
        },

        /**
         * stores the queued property changes to the backend, one after another.
         * Each change is processed individually, before the next in queue is processed.
         */
        async executeSave() {
            let saveNeeded = false;
            if (this.saveQueue.length) {
                // Get one queue entry:
                // can be either an object with {property, value}, or an array with {property, value} objects:
                // set them on the record, then save it:
                const change = this.saveQueue.shift();
                if (Array.isArray(change)) {
                    change.forEach((c) => this.record.set(c.property, c.value));
                } else {
                    this.record.set(change.property, change.value);
                }

                // save it to the backend:
                if (this.record.isDirty()) {
                    saveNeeded = true;
                }
                await this.runAsync(async () => this.record.save());

                // repeat until the queue is empty:
                saveNeeded = saveNeeded || (await this.executeSave());
            }
            return saveNeeded;
        },
        onLocationSaved() {
            this.$emit('recordSaved', this.record);
        },
        onScheduleSaved() {
            this.$emit('recordSaved', this.record);
        },
        getUrl(preview = false) {
            return frontendUrl(`/agenda/${this.record?.getId()}`, preview ? { preview: 'enable' } : null);
        },
        onFeaturedImagesCreated() {
            this.save();
        },
        getBrowserTitle() {
            return this.browserTitle;
        },
        async onEventDeleted() {
            this.$emit('recordDeleted', this.record);
        },
    },
};
</script>

<style lang="scss" scoped>
.compact-edit {
    display: grid;
    padding: 0.5rem;
    row-gap: 1rem;
    grid-template-columns: auto;
    grid-template-areas:
        'edit-event'
        'infos'
        'subevents'
        'metadata'
        'importdata';
}

.edit-event-container {
    grid-area: edit-event;
}
.infos-container {
    grid-area: infos;
}
.subevents-container {
    grid-area: subevents;
}
.metadata-container {
    grid-area: metadata;
}
.importdata-container {
    grid-area: importdata;
}
</style>
